import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import EventGeneralLedgerSettings from "../../ConfirmTicketing/components/EventGeneralLedgerSettings";
import FeeRuleTable from "../../ConfirmTicketing/components/FeeRuleTable";
import { BLANK_FEES, seriesFeeRuleOverrideStore, seriesGeneralLedgerStore } from "../seriesFeeStores";

const FEE_TABLE_USE_CASES = ["ONLINE", "AT_DOOR_CARD", "AT_DOOR_CASH"];

// One setting for the whole series, the same on every show type: SeriesDateBuilder
// copies it onto every date. As on a confirm, General Ledger replaces the
// organizer fee table for teams with GL codes.
const SeriesFeeSettings = ({ csrfToken, teamId, venueOwnershipId, fees, onChange, canAccessGeneralLedgerCodes, feeRuleSets }) => {
  const feesRef = useRef(fees || BLANK_FEES);
  feesRef.current = fees || BLANK_FEES;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const report = (next) => onChangeRef.current(next);
  const generalLedgerStore = useMemo(() => seriesGeneralLedgerStore(feesRef, report), []);
  const overrideStore = useMemo(() => seriesFeeRuleOverrideStore(feesRef, report), []);

  const team = { id: teamId, can_access_general_ledger_codes: canAccessGeneralLedgerCodes };
  const plan = { id: "series", general_ledger_code_id: feesRef.current.generalLedgerCodeId };
  const hasFeeTable = FEE_TABLE_USE_CASES.every((useCase) => (feeRuleSets || []).some((set) => set.use_case === useCase));

  return (
    <section className="series-tab-section" data-testid="series-fee-settings">
      <span className="series-edit-label">Organizer Fees · Every date</span>
      <p className="series-edit-hint">Applies to every date in the series, whatever its show type.</p>
      {canAccessGeneralLedgerCodes ? (
        <EventGeneralLedgerSettings csrfToken={csrfToken} confirm={plan} team={team} store={generalLedgerStore} forSeries />
      ) : hasFeeTable ? (
        <FeeRuleTable
          key={venueOwnershipId}
          csrfToken={csrfToken}
          confirm={plan}
          team={team}
          feeRuleSets={feeRuleSets}
          eventFeeComponentCount={0}
          store={overrideStore}
          forSeries
        />
      ) : (
        <p className="series-edit-hint">
          {venueOwnershipId ? "This venue has no organizer fee table to customize." : "Choose a venue in Series Details to set its organizer fees."}
        </p>
      )}
    </section>
  );
};

SeriesFeeSettings.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  venueOwnershipId: PropTypes.number,
  fees: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  canAccessGeneralLedgerCodes: PropTypes.bool,
  feeRuleSets: PropTypes.array,
};

export default SeriesFeeSettings;
